/** @file Helpers for reasoning about and manipulating the text currently under the cursor. */

export interface TextUnderCursor {
  text: string;
  anchorOffset: number;
  focusOffset: number;
}

export const DEFAULT_TEXT_UNDER_CURSOR = {
  text: '',
  anchorOffset: 0,
  focusOffset: 0,
};

export function isSelectionCollapsed(textUnderCursor: TextUnderCursor): boolean {
  const { anchorOffset, focusOffset } = textUnderCursor;

  return anchorOffset === focusOffset;
}

export function getTextBeforeCursor(textUnderCursor: TextUnderCursor): string {
  const { text, focusOffset } = textUnderCursor;

  return text.substring(0, focusOffset);
}

// Require the cursor to be followed by whitespace, punctuation, or the end of the string.
const aheadOfCursorRegExp = /^\S(\s|[.,!?:;"'\-–—()[\]…]|$)/;

export function isCursorAtBoundary(textUnderCursor: TextUnderCursor): boolean {
  const { text, focusOffset } = textUnderCursor;

  return focusOffset > 0 && aheadOfCursorRegExp.test(text.substring(focusOffset - 1, focusOffset + 1));
}

export function canAutocompleteAtCursor(textUnderCursor: TextUnderCursor, partial?: string): boolean {
  const textBeforeCursor = getTextBeforeCursor(textUnderCursor);

  return (
    isSelectionCollapsed(textUnderCursor) &&
    isCursorAtBoundary(textUnderCursor) &&
    (partial ? textBeforeCursor.endsWith(partial) : textBeforeCursor.length > 0)
  );
}

export function autocompleteAtCursor(
  textUnderCursor: TextUnderCursor,
  partial: string,
  complete: string,
): TextUnderCursor {
  if (!canAutocompleteAtCursor(textUnderCursor, partial)) {
    return textUnderCursor;
  }

  const { text, focusOffset: offset } = textUnderCursor;
  const newOffset = offset - partial.length + complete.length;

  return {
    text: text.substring(0, offset - partial.length) + complete + text.substring(offset),
    anchorOffset: newOffset,
    focusOffset: newOffset,
  };
}
